"use client";

import Image from "next/image";
import Link from "next/link";
import {
  Check,
  FileCheck,
  FileText,
  LayoutTemplate,
  Linkedin,
  MessageSquare,
  ScanLine,
  Target,
  type LucideIcon,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import type { OfferIconKey, OfferItem } from "@/components/offer/offer-catalog";

export interface OfferCardLabels {
  freeBadge: string;
  deprecatedBadge: string;
}

const ICONS: Record<OfferIconKey, LucideIcon> = {
  "file-check": FileCheck,
  "scan-line": ScanLine,
  "layout-template": LayoutTemplate,
  "file-text": FileText,
  "message-square": MessageSquare,
  target: Target,
  linkedin: Linkedin,
};

export function OfferProductCard({
  item,
  labels,
  tab,
  sourcePage = "dashboard_offer",
}: {
  item: OfferItem;
  labels: OfferCardLabels;
  tab: string;
  sourcePage?: string;
}) {
  const isDeprecated = item.cta === "deprecated";
  const Icon = ICONS[item.icon];
  const isLinkedin = item.icon === "linkedin";

  return (
    <div
      className={cn(
        "flex h-full flex-col gap-4 rounded-2xl border border-border-primary bg-card p-5",
        "transition duration-200 ease-out hover:-translate-y-0.5 hover:border-brand-primary/30 hover:shadow-card",
        isDeprecated && "opacity-70",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          className={cn(
            "flex size-11 items-center justify-center rounded-xl",
            isLinkedin
              ? "bg-white ring-1 ring-border-primary"
              : "bg-brand-primary/10 text-brand-primary",
          )}
        >
          {isLinkedin ? (
            <Image
              src="/assets/linkedin.svg"
              alt="LinkedIn"
              width={28}
              height={28}
              className="size-7"
            />
          ) : (
            <Icon className="size-5" aria-hidden="true" strokeWidth={2} />
          )}
        </span>
        {isDeprecated ? (
          <Badge
            variant="outline"
            className="border-border-primary text-text-muted"
          >
            {labels.deprecatedBadge}
          </Badge>
        ) : item.isFree ? (
          <Badge className="border-transparent bg-green-100 text-green-700 hover:bg-green-100">
            {labels.freeBadge}
          </Badge>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <h3 className="text-base font-semibold text-text-heading">
          {item.name}
        </h3>
        <p className="text-sm leading-6 text-text-muted">{item.description}</p>
      </div>

      <ul className="space-y-2">
        {item.features.map((feature) => (
          <li
            key={feature}
            className="flex items-start gap-2 text-sm text-text-body"
          >
            <Check
              className="mt-0.5 size-4 flex-shrink-0 text-green-600"
              aria-hidden="true"
            />
            {feature}
          </li>
        ))}
      </ul>

      <div className="mt-auto space-y-3 pt-1">
        {!item.isFree ? (
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-bold text-text-heading">
              {item.priceLabel}
            </span>
            {item.priceSuffix ? (
              <span className="text-xs text-text-muted">
                /{item.priceSuffix}
              </span>
            ) : null}
          </div>
        ) : null}

        {isDeprecated ? (
          <p className="text-xs text-text-muted">{item.note}</p>
        ) : (
          <Button
            asChild
            variant="outline"
            className="min-h-11 w-full border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white"
          >
            <Link
              href={item.href ?? "#"}
              onClick={() =>
                trackEvent("offer_product_click", {
                  id: item.id,
                  is_free: item.isFree,
                  tab,
                  source_page: sourcePage,
                })
              }
            >
              {item.ctaLabel}
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}
